import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react'
import type { KitlookData } from 'src/types/kitlooks'

interface KitlookContextData {
  kitlooks: KitlookData[]
  currentKit: KitlookData | null
  currentIndex: number
  nextKit: () => void
  prevKit: () => void
  hasNext: boolean
  hasPrev: boolean
}

const KitlookContext = createContext<KitlookContextData>(
  {} as KitlookContextData
)

export const KitlookProvider: React.FC<{
  kitlooks: KitlookData[]
  children: React.ReactNode
}> = ({ kitlooks, children }) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const hasNext = useMemo(
    () => currentIndex < kitlooks.length - 1,
    [currentIndex, kitlooks]
  )

  const hasPrev = useMemo(() => currentIndex > 0, [currentIndex])

  const currentKit = useMemo(
    () => kitlooks[currentIndex] || null,
    [currentIndex, kitlooks]
  )

  const nextKit = useCallback(() => {
    setCurrentIndex((prev) => (prev < kitlooks.length - 1 ? prev + 1 : prev))
  }, [kitlooks.length])

  const prevKit = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev))
  }, [])

  const value = useMemo(
    () => ({
      kitlooks,
      currentKit,
      currentIndex,
      nextKit,
      prevKit,
      hasNext,
      hasPrev,
    }),
    [kitlooks, currentKit, currentIndex, hasNext, hasPrev, nextKit, prevKit]
  )

  return (
    <KitlookContext.Provider value={value}>{children}</KitlookContext.Provider>
  )
}

export const useKitlook = () => useContext(KitlookContext)
